import { GameSession } from '@/services/game-session'
import { useInstantWinsStore } from '@/stores/instant-wins'
import type { GameSessionPageData, InstantWinDraw } from '@/types'
import type { RouteRecordProps } from 'env'
import type {
  NavigationGuardNext,
  RouteLocation,
  RouteRecordRaw,
} from 'vue-router'

import { beforeRouteInstantWinUpdate } from './guards/fetch-instant-win'
import AuthMiddleware from './middleware/auth'

export default [
  {
    path: '/instant-win/:instantWinCode',
    name: 'instant-win',
    component: () => import('../views/InstantWinsView.vue'),
    beforeEnter: [beforeRouteInstantWinUpdate],
    meta: {
      title: (to: RouteLocation): string => {
        const instantWin = to.meta.props as InstantWinDraw | undefined
        const fallbackCode = to.params.instantWinCode
        const suffix =
          instantWin?.title ??
          (typeof fallbackCode === 'string'
            ? fallbackCode.toUpperCase()
            : 'Unknown')
        return `Instant Prize Draw: ${suffix}`
      },
    },
    props: (route) => {
      return {
        instantWin: route.meta.props,
      }
    },
  },
  {
    path: '/instant-win-checkout/:instantWinCode/order-confirmation',
    name: 'instant-win-checkout-order-confirmation',
    component: () =>
      import(
        '../views/Checkout/CheckoutStages/CheckoutInstantWinsConfirmationDesktopView.vue'
      ),
    meta: { title: 'Instant Prizes Checkout Order Confirmation' },
    props: (route) => {
      const routeProps = route.meta.props as {
        instantWin: InstantWinDraw
        gameSessionData: GameSessionPageData
      }

      return {
        instantWin: routeProps.instantWin,
        gameSessionData: routeProps.gameSessionData,
      }
    },
    beforeEnter: [
      AuthMiddleware,
      async (
        to: RouteLocation,
        _from: RouteLocation,
        next: NavigationGuardNext
      ) => {
        try {
          const instantWinCode = to.params.instantWinCode as string
          const instantWinsStore = useInstantWinsStore()

          const instantWin =
            await instantWinsStore.getInstantWin(instantWinCode)
          const gameSessionId = to.query.gameSessionId

          if (
            !instantWin ||
            !gameSessionId ||
            typeof gameSessionId !== 'string'
          ) {
            return next({ name: 'not-found' })
          }

          // Validate gameSessionId by calling the API and store the result
          try {
            const gameSessionData =
              await GameSession.getGameSession(gameSessionId)
            to.meta.props = {
              instantWin,
              gameSessionData: gameSessionData,
            }
          } catch (error) {
            console.error('Invalid gameSessionId:', gameSessionId, error)
            return next({ name: 'not-found' })
          }

          next()
        } catch (e) {
          console.error(e)
          next({ name: 'not-found' })
        }
      },
    ],
  },

  {
    path: '/instant-win-checkout/:instantWinCode/open',
    name: 'instant-win-checkout-open',
    component: () =>
      import(
        '../views/Checkout/CheckoutStages/CheckoutInstantWinsRouletteView.vue'
      ),
    meta: { title: 'Open Mystery Prize Boxes' },
    props: (route) => {
      const routeProps = route.meta.props as {
        instantWin: InstantWinDraw
        gameSessionData: GameSessionPageData
      }

      return {
        instantWin: routeProps.instantWin,
        gameSessionData: routeProps.gameSessionData,
      }
    },
    beforeEnter: [
      AuthMiddleware,
      async (
        to: RouteLocation,
        _from: RouteLocation,
        next: NavigationGuardNext
      ) => {
        try {
          const instantWinCode = to.params.instantWinCode as string
          const instantWinsStore = useInstantWinsStore()

          const instantWin =
            await instantWinsStore.getInstantWin(instantWinCode)
          const gameSessionId = to.query.gameSessionId

          if (
            !instantWin ||
            !gameSessionId ||
            typeof gameSessionId !== 'string'
          ) {
            return next({ name: 'not-found' })
          }

          // Validate gameSessionId by calling the API and store the result
          try {
            const gameSessionData =
              await GameSession.getGameSession(gameSessionId)

            to.meta.props = {
              instantWin,
              gameSessionData: gameSessionData,
            }
          } catch (error) {
            console.error('Invalid gameSessionId:', gameSessionId, error)
            return next({ name: 'not-found' })
          }

          next()
        } catch (e) {
          console.error(e)
          next({ name: 'not-found' })
        }
      },
    ],
  },

  {
    path: '/instant-win-checkout/:instantWinCode/winner',
    name: 'instant-win-checkout-winner',
    component: () =>
      import(
        '../views/Checkout/CheckoutStages/CheckoutInstantWinsWinnerDesktopView.vue'
      ),
    meta: { title: 'Instant Prizes - Winner' },
    props: (route) => {
      const routeProps = route.meta.props as {
        instantWin: InstantWinDraw
        gameSessionData: GameSessionPageData
      }

      return {
        instantWin: routeProps.instantWin,
        gameSessionData: routeProps.gameSessionData,
      }
    },
    beforeEnter: [
      AuthMiddleware,
      async (
        to: RouteLocation,
        _from: RouteLocation,
        next: NavigationGuardNext
      ) => {
        try {
          const instantWinCode = to.params.instantWinCode as string
          const instantWinsStore = useInstantWinsStore()

          const instantWin =
            await instantWinsStore.getInstantWin(instantWinCode)
          const gameSessionId = to.query.gameSessionId

          if (
            !instantWin ||
            !gameSessionId ||
            typeof gameSessionId !== 'string'
          ) {
            return next({ name: 'not-found' })
          }

          // Validate gameSessionId by calling the API and store the result
          try {
            const gameSessionData =
              await GameSession.getGameSession(gameSessionId)

            to.meta.props = {
              instantWin,
              gameSessionData: gameSessionData,
            }
          } catch (error) {
            console.error('Invalid gameSessionId:', gameSessionId, error)
            return next({ name: 'not-found' })
          }

          next()
        } catch (e) {
          console.error(e)
          next({ name: 'not-found' })
        }
      },
    ],
  },
  {
    path: '/instant-win-checkout/:instantWinCode/next-time',
    name: 'instant-win-checkout-no-winner',
    component: () =>
      import(
        '../views/Checkout/CheckoutStages/CheckoutInstantWinsNoWinnerDesktopView.vue'
      ),
    meta: { title: 'Instant Prizes - Next Time' },
    props: (route) => {
      const routeProps = route.meta.props as {
        instantWin: InstantWinDraw
        gameSessionData: GameSessionPageData
      }

      return {
        instantWin: routeProps.instantWin,
        gameSessionData: routeProps.gameSessionData,
      }
    },
    beforeEnter: [
      AuthMiddleware,
      async (
        to: RouteLocation,
        _from: RouteLocation,
        next: NavigationGuardNext
      ) => {
        try {
          const instantWinCode = to.params.instantWinCode as string
          const instantWinsStore = useInstantWinsStore()

          const instantWin =
            await instantWinsStore.getInstantWin(instantWinCode)
          const gameSessionId = to.query.gameSessionId

          if (
            !instantWin ||
            !gameSessionId ||
            typeof gameSessionId !== 'string'
          ) {
            return next({ name: 'not-found' })
          }

          // Validate gameSessionId by calling the API and store the result
          try {
            const gameSessionData =
              await GameSession.getGameSession(gameSessionId)

            to.meta.props = {
              instantWin,
              gameSessionData: gameSessionData,
            }
          } catch (error) {
            console.error('Invalid gameSessionId:', gameSessionId, error)
            return next({ name: 'not-found' })
          }

          next()
        } catch (e) {
          console.error(e)
          next({ name: 'not-found' })
        }
      },
    ],
  },
  {
    path: '/instant-win-terms-and-conditions/:instantWinCode',
    name: 'instant-win-terms-and-conditions',
    component: () =>
      import('../views/TermsAndConditions/InstantWinTermsView.vue'),
    meta: {
      title: (to: RouteLocation): string => {
        return `Instant Prize Draw: ${(to.meta.props as InstantWinDraw).title}`
      },
    },
    props: (route) => {
      return {
        instantWin: route.meta.props,
      }
    },
    async beforeEnter(
      to: RouteLocation,
      _from: RouteLocation,
      next: NavigationGuardNext
    ) {
      try {
        const instantWinCode = to.params.instantWinCode as string

        if (!instantWinCode) {
          return next({ name: 'not-found' })
        }

        const instantWinsStore = useInstantWinsStore()
        const instantWin = await instantWinsStore.getInstantWin(instantWinCode)

        if (!instantWin) {
          return next({ name: 'not-found' })
        }

        to.meta.props = instantWin

        next()
      } catch (e) {
        console.error(e)
        // redirect to NotFoundView
        return next({ name: 'not-found' })
      }
    },
  },
  {
    path: '/instant-win-checkout/:instantWinCode/play',
    name: 'instant-wins-play',
    meta: {
      title: (to: RouteLocation): string => {
        return `Instant Prize Draw: ${((to.meta.props as any).instantWin as InstantWinDraw).title}`
      },
    },
    component: () => import('../views/InstantWinsView.vue'),
    props: (
      route: RouteRecordProps<{
        instantWin: InstantWinDraw
        gameSessionData: GameSessionPageData
      }>
    ) => {
      const routeProps = route.meta.props as {
        instantWin: InstantWinDraw
        gameSessionData: GameSessionPageData
      }

      return {
        instantWin: routeProps.instantWin,
        gameSessionData: routeProps.gameSessionData,
      }
    },
    beforeEnter: [
      AuthMiddleware,
      async (
        to: RouteLocation,
        _from: RouteLocation,
        next: NavigationGuardNext
      ) => {
        try {
          const instantWinCode = to.params.instantWinCode as string
          const instantWinsStore = useInstantWinsStore()

          const instantWin =
            await instantWinsStore.getInstantWin(instantWinCode)
          const gameSessionId = to.query.gameSessionId

          if (
            !instantWin ||
            !gameSessionId ||
            typeof gameSessionId !== 'string'
          ) {
            return next({ name: 'not-found' })
          }

          // Validate gameSessionId by calling the API and store the result
          try {
            const gameSessionData =
              await GameSession.getGameSession(gameSessionId)

            to.meta.props = {
              instantWin,
              gameSessionData: gameSessionData,
            }
          } catch (error) {
            console.error('Invalid gameSessionId:', gameSessionId, error)
            return next({ name: 'not-found' })
          }

          next()
        } catch (e) {
          console.error(e)
          next({ name: 'not-found' })
        }
      },
    ],
  },
  {
    path: '/instant-win/:instantWinCode/demo',
    name: 'instant-wins-demo',
    meta: {
      title: (to: RouteLocation): string => {
        return `Instant Prize Draw Demo: ${((to.meta.props as any).instantWin as InstantWinDraw).title}`
      },
    },
    component: () => import('../views/InstantWinsView.vue'),
    props: (
      route: RouteRecordProps<{
        instantWin: InstantWinDraw
        gameSessionData: GameSessionPageData
      }>
    ) => {
      const { instantWin, gameSessionData } = route.meta.props
      return {
        instantWin,
        gameSessionData,
      }
    },
    beforeEnter: [
      async (
        to: RouteLocation,
        _from: RouteLocation,
        next: NavigationGuardNext
      ) => {
        try {
          const instantWinCode = to.params.instantWinCode as string
          const instantWinsStore = useInstantWinsStore()

          const instantWin =
            await instantWinsStore.getInstantWin(instantWinCode)

          if (!instantWin) {
            return next({ name: 'not-found' })
          }

          const gameSessionData = GameSession.createDemoSession(instantWin)
          to.meta.props = {
            instantWin,
            gameSessionData,
          }

          next()
        } catch (e) {
          console.error(e)
          next({ name: 'not-found' })
        }
      },
    ],
  },
] as Readonly<RouteRecordRaw[]>
